/**
 * Quantity Pack Selector
 * Sets form quantity based on selected pack tier.
 * Reads pricing data from data attributes set by Liquid.
 */

class QuantityPackSelector {
  constructor(element) {
    if (element.dataset.initialized === 'true') return;
    element.dataset.initialized = 'true';

    this.element = element;
    this.radioInputs = this.element.querySelectorAll('.qty-pack-selector__radio');
    this.cards = this.element.querySelectorAll('.qty-pack-row');
    this.productForm = this.element.closest('product-form') || this.element.closest('form');
    this.quantityInput = this.element.querySelector('.qty-pack-selector__quantity-input');
    this.variantInputs = this.productForm?.querySelectorAll('input[name="id"]') || [];
    this.addToCartBtn = this.productForm?.querySelector('.js-add-to-cart');

    this.init();
  }

  init() {
    this.radioInputs.forEach((radio) => {
      radio.addEventListener('change', this.handleTierChange.bind(this));
    });

    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.classList.contains('qty-pack-selector__radio')) return;
        const radio = card.querySelector('.qty-pack-selector__radio');
        if (radio && !radio.disabled) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Initialize with currently selected tier
    const selectedRadio = this.element.querySelector('.qty-pack-selector__radio:checked');
    if (selectedRadio) {
      this.updateSelection(selectedRadio);
      this.updateFormVariant(selectedRadio.closest('.qty-pack-row'));
      this.updateButtonPrice(selectedRadio.closest('.qty-pack-row'));
      this.updateGiftEligibility(selectedRadio.closest('.qty-pack-row'));
    }
  }

  handleTierChange(event) {
    const radio = event.target;
    const quantity = radio.value;
    if (!quantity) return;

    this.updateSelection(radio);
    this.updateFormQuantity(quantity);
    this.updateFormVariant(radio.closest('.qty-pack-row'));
    this.updateButtonPrice(radio.closest('.qty-pack-row'));
    this.updateGiftEligibility(radio.closest('.qty-pack-row'));
  }

  updateSelection(selectedRadio) {
    this.cards.forEach((card) => {
      card.classList.remove('qty-pack-row--selected');
    });
    const selectedCard = selectedRadio.closest('.qty-pack-row');
    if (selectedCard) {
      selectedCard.classList.add('qty-pack-row--selected');
    }
  }

  updateFormQuantity(quantity) {
    // Update the hidden quantity input inside the pack selector
    if (this.quantityInput) {
      this.quantityInput.value = quantity;
    }

    // Also update any other quantity input in the product form
    const formQuantityInput = this.productForm?.querySelector('input[name="quantity"]:not(.qty-pack-selector__quantity-input)');
    if (formQuantityInput) {
      formQuantityInput.value = quantity;
    }
  }

  updateFormVariant(card) {
    if (!card || !this.variantInputs.length || !card.dataset.tierVariantId) return;

    this.variantInputs.forEach((input) => {
      input.value = card.dataset.tierVariantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateButtonPrice(card) {
    if (!this.addToCartBtn || !card) return;

    const totalFormatted = card.dataset.tierTotalPriceFormatted;
    if (!totalFormatted) return;

    // Find or create the price span inside the button
    let priceSpan = this.addToCartBtn.querySelector('.qty-pack-atc-price');
    if (!priceSpan) {
      priceSpan = document.createElement('span');
      priceSpan.className = 'qty-pack-atc-price';
      const textSpan = this.addToCartBtn.querySelector('span:not(.qty-pack-atc-price)');
      if (textSpan) {
        textSpan.after(document.createTextNode(' '), priceSpan);
      } else {
        this.addToCartBtn.appendChild(priceSpan);
      }
    }

    const compareFormatted = card.dataset.tierCompareTotalFormatted;
    if (compareFormatted) {
      priceSpan.innerHTML = `\u00b7 <s>${compareFormatted}</s> ${totalFormatted}`;
    } else {
      priceSpan.textContent = `\u00b7 ${totalFormatted}`;
    }
  }

  updateGiftEligibility(card) {
    const giftProperty = this.element.querySelector('.volume-tier-selector__gift-property');
    if (!giftProperty || !card) return;

    const quantity = Number(
      card.dataset.tierQty ||
      card.querySelector('.qty-pack-selector__radio')?.value ||
      0
    );

    // The Pastillero marker is submitted for the 2- and 3-unit volume offers.
    giftProperty.disabled = quantity < 2;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-pack-selector]').forEach((el) => {
    if (el.dataset.initialized !== 'true') new QuantityPackSelector(el);
  });
});

if (window.MutationObserver) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-pack-selector]')) {
          if (node.dataset.initialized !== 'true') new QuantityPackSelector(node);
        }
        node.querySelectorAll?.('[data-pack-selector]').forEach((el) => {
          if (el.dataset.initialized !== 'true') new QuantityPackSelector(el);
        });
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
